/**
 * Hash router. Small enough to own: the app has a handful of routes, and hash
 * URLs mean the built app can be served from any path without server rewrites.
 */
import { useSyncExternalStore, useCallback } from 'react';

const read = () => {
  const raw = window.location.hash.replace(/^#/, '') || '/';
  const [path, query = ''] = raw.split('?');
  return { path, query };
};

const subscribe = (fn) => {
  window.addEventListener('hashchange', fn);
  return () => window.removeEventListener('hashchange', fn);
};

let snapshot = read();
let cachedHash = window.location.hash;
const getSnapshot = () => {
  if (window.location.hash !== cachedHash) {
    cachedHash = window.location.hash;
    snapshot = read();
  }
  return snapshot;
};

export function navigate(to, { replace = false } = {}) {
  const target = `#${to.startsWith('/') ? to : `/${to}`}`;
  if (window.location.hash === target) return;
  if (replace) window.location.replace(target);
  else window.location.hash = target;
}

/**
 * Returns { path, segments, query, params } for the current hash and a navigate
 * helper. `match` turns "/boards/:id" into params.
 */
export function useRoute() {
  const { path, query } = useSyncExternalStore(subscribe, getSnapshot);
  const segments = path.split('/').filter(Boolean);

  const match = useCallback((pattern) => {
    const parts = pattern.split('/').filter(Boolean);
    if (parts.length !== segments.length) return null;
    const params = {};
    for (let i = 0; i < parts.length; i += 1) {
      if (parts[i].startsWith(':')) params[parts[i].slice(1)] = decodeURIComponent(segments[i]);
      else if (parts[i] !== segments[i]) return null;
    }
    return params;
  }, [path]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    path,
    segments,
    query: Object.fromEntries(new URLSearchParams(query)),
    match,
    navigate,
  };
}
